import type { CurrentUser } from '@zhiyue/core';
import {
  type ApprovalTransferMember,
  listApprovalTransferMembers,
} from '@/services/bff';
import { fetchOpenPlatformToken } from '@/services/openPlatformToken';

type MemberDirectoryUser = Pick<CurrentUser, 'tenantUuid' | 'memberUuid'> & {
  organizationUuid?: string;
};

let identityKey = '';
let loading: Promise<void> | null = null;
const members = new Map<string, ApprovalTransferMember>();

const getIdentityKey = (currentUser?: Partial<MemberDirectoryUser> | null) =>
  [
    currentUser?.tenantUuid,
    currentUser?.organizationUuid,
    currentUser?.memberUuid,
  ]
    .map((value) => String(value || '').trim())
    .join('|');

export const clearMemberDirectory = () => {
  identityKey = '';
  loading = null;
  members.clear();
};

export const loadMemberDirectory = async (
  currentUser?: Partial<MemberDirectoryUser> | null,
): Promise<void> => {
  const nextIdentityKey = getIdentityKey(currentUser);
  const memberUuid = String(currentUser?.memberUuid || '').trim();
  if (!nextIdentityKey || !memberUuid) return clearMemberDirectory();
  if (identityKey === nextIdentityKey && (loading || members.size))
    return loading || undefined;

  identityKey = nextIdentityKey;
  members.clear();
  loading = fetchOpenPlatformToken(currentUser as MemberDirectoryUser)
    .then(({ accessToken }) =>
      listApprovalTransferMembers(accessToken, memberUuid),
    )
    .then((items) => {
      if (identityKey !== nextIdentityKey) return;
      items.forEach((member) => {
        const memberUuid = member.memberUuid || member.id;
        if (memberUuid) members.set(memberUuid, member);
      });
    })
    .finally(() => {
      if (identityKey === nextIdentityKey) loading = null;
    });
  return loading;
};

export const getMemberName = (memberUuid?: string | null): string => {
  if (!memberUuid) return '-';
  return members.get(memberUuid)?.name || memberUuid;
};
